import { createSelector } from 'reselect';
import { getNeedsToIdVerify } from '../utils/applicantDetailsUtil';
import ListingType from '../constants/listingTypes';

export const brownoutWarningMessageSelector = (state) =>
  state.rentalApplication.applicationsBrownout.applicationsBrownoutWarningMessage;
export const brownoutMessageSelector = (state) =>
  state.rentalApplication.applicationsBrownout.applicationsBrownoutMessage;

export const screeningBrownoutMessageSelector = (state) =>
  state.rentalApplication.applicationsBrownout.screeningBrownoutMessage;

export const listingSelector = (state) => state.listing;
export const rentGuaranteeSelector = (state) => state.listing.rentGuarantee;
export const isRentGuaranteeEnabledSelector = createSelector(
  rentGuaranteeSelector,
  (rentGuarantee) => rentGuarantee?.isDeferred || (rentGuarantee?.isEligible && rentGuarantee?.isEnabled),
);
export const productTypeSelector = (state) => state.listing.productType || 'created';
export const rentalApplicationSelector = (state) => state.rentalApplication;
export const currentListingSelector = createSelector(
  rentalApplicationSelector,
  (rentalApplication) => rentalApplication.currentListing,
);
export const newInviteStatusSelector = createSelector(
  currentListingSelector,
  (currentListing) => currentListing.newInviteStatus,
);
export const inviteInfoFromLeadInquirySelector = createSelector(
  currentListingSelector,
  (currentListing) => currentListing.inviteInfoFromLeadInquiry,
);

export const leadInfoSelector = createSelector(inviteInfoFromLeadInquirySelector, (inviteInfoFromLeadInquiry) => {
  if (!inviteInfoFromLeadInquiry) {
    return {
      renterFirstName: '',
      renterLastName: '',
      toEmail: '',
    };
  }
  const name = inviteInfoFromLeadInquiry.name || '';
  const toEmail = inviteInfoFromLeadInquiry.email || '';
  const splitNames = name.split(' ');
  const renterFirstName = splitNames[0];

  // last name will be whatever names are remaining in the array
  const renterLastName = splitNames.slice(1).join(' ');
  return {
    renterFirstName,
    renterLastName,
    toEmail,
  };
});
export const propertyAddressSelector = createSelector(listingSelector, (listing) => {
  if (!listing.street) {
    return '';
  }
  if (!listing.unit) {
    return listing.street;
  }
  return `${listing.street} ${listing.unit}`;
});

export const listingAliasSelector = ({ listing: { listingAlias } }) => listingAlias;
export const propertyIdSelector = ({ listing: { propertyId } }) => propertyId;
export const userIdIreneSelector = ({ user: { userToken } }) => userToken;
export const emailSelector = ({ user: { email } }) => email;

export const isLandlordIdVerifiedSelector = (state) => state.rentalApplication.landlord.isIdVerified;
export const landlordTermsStatusSelector = (state) => state.rentalApplication.landlord.termsStatus;
export const getNeedsToIdVerifySelector = () =>
  createSelector(
    isLandlordIdVerifiedSelector,
    landlordTermsStatusSelector,
    (isLandlordIdVerified, landlordTermsStatus) => {
      return getNeedsToIdVerify({
        termsStatus: landlordTermsStatus,
        isLandlordIdVerified,
      });
    },
  );
export const idVerifyingCompletedLinkSelector = (state) => state.rentalApplication.landlord.idVerifyingCompletedLink;
export const idVerifyingPreviousLinkSelector = (state) => state.rentalApplication.landlord.idVerifyingPreviousLink;

export const isLandlordRestrictedSelector = (state) =>
  state.listing.restrictions.applications?.isUpdateRestricted || false;

export const isListingRestrictedSelector = (state) =>
  state.listing.restrictions.applications?.isAccessRestricted || false;

export const isRoomForRentSelector = (state) => state.listing.listingType.code === ListingType.roomForRent.code;

export const applicationsEnabledSelector = (state) => state.rentalApplication.currentListing.enrollmentOn;

export const isMobileSelector = (state) => state.app.isMobile;

export const inviteToApplyModalIsOpenSelector = (state) => state.rentalApplication.inviteToApplyModal;

export const requestIdVerificationModalIsOpenSelector = (state) =>
  state.rentalApplication.requestIdVerificationModalIsOpen;

export const isCreditReportLoadingSelector = (state) =>
  state.rentalApplication.loadingStatus.reportsLoadingStatuses.creditReportLoading;

export const isBackgroundReportLoadingSelector = (state) =>
  state.rentalApplication.loadingStatus.reportsLoadingStatuses.backgroundReportLoading;

export const isEnrollmentLoadingSelector = (state) =>
  state.rentalApplication.loadingStatus.enrollmentLoadingStatus.enrollmentLoading;

export const getApplicationManagerNeedsToIdv = (state) => state.rentalApplication.landlord.applicationManagerNeedsToIdv;

// Filter nulls since invited co-applicants who have not started will have a null userId
export const applicantZuidsSelector = (state) =>
  state.rentalApplication?.currentApplication?.applicantsData
    ?.map((applicant) => applicant.userId)
    .filter((userId) => userId);

export const primaryApplicantInfoSelector = createSelector(
  rentalApplicationSelector,
  (rentalApplication) =>
    rentalApplication?.currentApplication?.applicantsData?.find(({ isPrimaryApplicant }) => isPrimaryApplicant)
      ?.questionMap ?? {},
);

export const primaryApplicantNameSelector = createSelector(
  primaryApplicantInfoSelector,
  // name is briefly undefined... we don't want to hold up rendering page
  // but we don't want the word "undefined" to flash in render, so default to empty string
  ({ firstName = '', lastName = '' }) => ({ firstName, lastName }),
);

export const userSelector = (state) => state.user;

export const activeListingCountSelector = (state) => state.user.summary.activeListingCount;

export const totalListingCountSelector = (state) => state.user.summary.totalListingCount;

export const isMultifamilyCustomerSelector = (state) => state.user.isMultifamilyCustomer;

export const isSingleFamilyEnterpriseCustomerSelector = (state) => state.user.isSingleFamilyEnterpriseCustomer;

export const isApplicationReviewStatusLoadingSelector = createSelector(
  rentalApplicationSelector,
  (rentalApplication) => {
    const loadingStatus = rentalApplication.loadingStatus?.loadingScreenStatus?.applicationReviewStatus ?? {};

    return loadingStatus.show && loadingStatus.showSpinner;
  },
);

export const zrmUserInfoSelector = createSelector(userSelector, (user) => ({
  zrm_user_info: {
    active_listing_cnt: user.summary.activeListingCount,
    total_listing_cnt: user.summary.totalListingCount,
    multi_family_customer_ind: user.isMultifamilyCustomer,
    single_family_enterprise_customer_ind: user.isSingleFamilyEnterpriseCustomer,
  },
}));

export const zrmListingInfoSelector = createSelector(
  isRentGuaranteeEnabledSelector,
  listingSelector,
  (isRentGuaranteeEnabled, listing) => ({
    zrm_listing_info: {
      listing_id: listing.listingAlias,
      rental_protection_ind: isRentGuaranteeEnabled,
      applications_ind: listing.isApplicationsAccepted,
      previously_activated_ind: !listing.hasNeverBeenActivated,
      product_type_txt: listing.eventProductType,
    },
  }),
);

export const zrmInfoClickstreamSelector = createSelector(
  zrmUserInfoSelector,
  zrmListingInfoSelector,
  (zrmUserInfo, zrmListingInfo) => ({ ...zrmUserInfo, ...zrmListingInfo }),
);

export const zgGraphUriSelector = (state) => state.app.zgGraphUri;

export const rentalPrefixUrlSelector = (state) => state.app.rentalPrefixUrl;

export const zgApiSelector = (state) => state.app.zgApi;

export const zgBaseUrlSelector = (state) => state.app.zgBaseUrl;

export const isZtssSelector = (state) => state.app.isZtss;

export const isImpersonatorSelector = (state) =>
  Boolean(state.user.impersonatorUserToken || state.user.impersonatorEmail);
